/**
 * cartDiscountUtils.ts — Puente entre carrito, descuentos y tickets de caja.
 *
 * Exporta helpers puros; no UI ni persistencia.
 */

import type { CartItem } from "@/types";
import type { TicketItem } from "@/features/caja/components/TicketItemsTable";
import {
  buildLineAdjustmentLabel,
  calculateDiscountBreakdown,
  type CartDiscounts,
  type DiscountBreakdown,
  type LineAdjustment,
  type LineDiscount,
} from "./discountHelper";
import type { VentaDescuentosBreakdownSnapshot } from "./ventaDiscountReplay";
import { formatTicketItemTaxLine } from "@/lib/constants/taxRates";
import { groupTicketItemBlocks } from "./ticketItemBlocks";
import {
  getLineCobradoTotal,
  getLineListaTotal,
  getLineUnitLista,
} from "./discountAttribution";
import { resolveLineTaxRatePct } from "./ticketTaxBreakdown";

export function getCartLineId(index: number, item: CartItem): string {
  return `${item.id}#${index}#${item.variantKey ?? ""}#${item.modifierKey ?? ""}`;
}

/** Precio unitario de lista (antes de ajustes de línea). */
export function getLineUnitListPrice(item: CartItem): number {
  return getLineUnitLista(item);
}

/**
 * Subtotal bruto de la línea: SIEMPRE el precio de lista (L × qty/peso).
 * El ahorro por precio mayorista/VIP y los descuentos de ítem se reportan por
 * separado (ver discountAttribution), así los Totales cuadran desde la lista
 * hasta el cobrado real.
 */
export function getLineBruto(item: CartItem): number {
  return getLineListaTotal(item);
}

export function buildCartDiscountsFromCart(
  cart: CartItem[],
  global: LineDiscount | null,
): CartDiscounts {
  const lineAdjustments: Record<string, LineAdjustment> = {};
  cart.forEach((item, index) => {
    if (item.lineAdjustment) {
      lineAdjustments[getCartLineId(index, item)] = item.lineAdjustment;
    }
  });
  return { byItem: {}, global, lineAdjustments };
}

export function buildCartDiscountInputs(cart: CartItem[]) {
  return cart.map((item, index) => ({
    lineId: getCartLineId(index, item),
    nombre: item.nombre,
    bruto: getLineBruto(item),
    // Neto REAL cobrado (precio del carrito ya resuelto): el motor NO vuelve a
    // aplicar ajustes sobre la lista; solo descompone para Totales.
    netoReal: getLineCobradoTotal(item),
    quantity: item.peso ? 1 : Math.max(1, item.quantity || 1),
    precioUnitarioOriginal: getLineUnitListPrice(item),
  }));
}

export function computeCartDiscountBreakdown(
  cart: CartItem[],
  global: LineDiscount | null,
): DiscountBreakdown {
  return calculateDiscountBreakdown(
    buildCartDiscountInputs(cart),
    buildCartDiscountsFromCart(cart, global),
  );
}

export function formatGlobalDiscountLabel(
  discount: LineDiscount | null,
): string {
  if (!discount || discount.value <= 0) return "";
  if (discount.type === "%") return `Descuento General (${discount.value}%)`;
  return "Descuento General";
}

export function hasActiveDiscounts(
  breakdown: DiscountBreakdown,
  global: LineDiscount | null,
): boolean {
  return (
    breakdown.totalDescuentoItems > 0 ||
    breakdown.descuentoGlobal > 0 ||
    !!(global && global.value > 0)
  );
}

function buildItemDiscountLabel(disc: DiscountBreakdown["lines"][0]): string {
  const pct =
    disc.ajusteAplicado?.mode === "discountPercent"
      ? Math.max(0, Math.round(disc.ajusteAplicado.discountPercent ?? 0))
      : 0;
  if (pct > 0) return `Descuento por �tem (${pct}%)`;
  return (
    buildLineAdjustmentLabel({
      ajusteAplicado: disc.ajusteAplicado,
      precioUnitarioOriginal: disc.precioUnitarioOriginal,
      precioUnitarioFinal: disc.precioUnitarioFinal,
    }) ?? "Descuento por �tem"
  );
}

/**
 * Inserta descuentos por ítem (solo ajuste individual, no global).
 * Total de columna = neto; P.UNIT usa precio lista vía precioUnitario.
 */
export function mergeDiscountLinesIntoTicket(
  ticketItems: TicketItem[],
  breakdown: DiscountBreakdown,
): TicketItem[] {
  if (breakdown.totalDescuentoItems <= 0) return ticketItems;

  const discountByName = new Map<string, (typeof breakdown.lines)[0]>();
  for (const line of breakdown.lines) {
    if (line.descuentoItem > 0 && line.ajusteAplicado) {
      discountByName.set(line.nombre, line);
    }
  }
  if (discountByName.size === 0) return ticketItems;

  const blocks = groupTicketItemBlocks(ticketItems);
  const out: TicketItem[] = [];

  for (const block of blocks) {
    const main = block[0];
    const disc = discountByName.get(main.nombre);
    const coreRows = block.filter(
      (it) => !it.isDiscountLine && !it.isArticleTotalLine,
    );

    if (disc && disc.descuentoItem > 0) {
      const qty = Math.max(1, disc.cantidad ?? main.cantidad ?? 1);
      const unitList =
        disc.precioUnitarioOriginal ??
        (qty > 0 ? Math.round(disc.bruto / qty) : (main.precioUnitario ?? 0));
      const netTotal = Math.max(0, disc.bruto - disc.descuentoItem);

      if (main.isGroupHeader) {
        out.push({
          ...main,
          lineSubtotal: netTotal,
          precioUnitario: unitList,
        });
        out.push(...coreRows.slice(1));
      } else {
        out.push({
          ...main,
          precioUnitario: unitList,
          lineSubtotal: netTotal,
        });
        out.push(...coreRows.slice(1).filter((it) => it !== main));
      }

      out.push({
        nombre: buildItemDiscountLabel(disc),
        cantidad: 0,
        precioUnitario: 0,
        lineSubtotal: -disc.descuentoItem,
        isSubLine: true,
        isDiscountLine: true,
        parentNombre: main.nombre,
      });
    } else {
      out.push(...coreRows);
    }
  }

  return out;
}

/** Añade sub-línea de impuesto por ítem cuando aplica IVA. */
export function appendTaxLinesToTicket(
  ticketItems: TicketItem[],
  cart: CartItem[],
  defaultRatePct = 0,
): TicketItem[] {
  const ivaByName = new Map<string, number>();
  cart.forEach((item) => {
    const rate = resolveLineTaxRatePct(item, defaultRatePct);
    if (rate > 0) {
      ivaByName.set(item.nombre, Math.round(rate));
    }
  });
  if (ivaByName.size === 0) return ticketItems;

  const blocks = groupTicketItemBlocks(ticketItems);
  const out: TicketItem[] = [];

  for (const block of blocks) {
    const main = block[0];
    const rate = ivaByName.get(main.nombre);
    const core = block.filter(
      (it) => !it.isDiscountLine && !it.isArticleTotalLine && !it.isTaxLine,
    );
    const tail = block.filter(
      (it) => it.isDiscountLine || it.isArticleTotalLine,
    );

    const coreRows = core.map((row, index) =>
      index === 0 && rate ? { ...row, taxRatePct: rate } : row,
    );

    if (!rate) {
      out.push(...coreRows);
      out.push(...tail);
      continue;
    }

    // Desglose de IVA POR PRODUCTO (incluido en el PVP). Se coloca justo
    // DESPUÉS del/los precio(s) unitarios de la línea (P.UNIT y el unitario
    // nuevo de mayorista) y ANTES de los descuentos, para que el lector pueda
    // operar en orden: multiplicar unidades → sumar IVA → restar descuentos.
    const subtotalLinea =
      main.lineSubtotal ??
      (main.peso && main.peso > 0
        ? Math.round((main.peso ?? 0) * (main.precioPorKilo ?? 0))
        : Math.round(main.precioUnitario * Math.max(1, main.cantidad)));
    const netoArticulo = Math.max(0, subtotalLinea);
    const baseGravable = Math.round(netoArticulo / (1 + rate / 100));
    const impuestoIncluido = Math.max(0, netoArticulo - baseGravable);
    const taxRow: TicketItem = {
      nombre: formatTicketItemTaxLine(rate),
      cantidad: 0,
      precioUnitario: 0,
      lineSubtotal: impuestoIncluido,
      isSubLine: true,
      isTaxLine: true,
      parentNombre: main.nombre,
    };

    // Último precio unitario nuevo (mayorista, fila positiva) si existe.
    const posIdx = coreRows.findIndex(
      (r) => r.esDescuentoPrecioLine === true && (r.lineSubtotal ?? 0) > 0,
    );
    const hayFilasPrecio = coreRows.some(
      (r) => r.esDescuentoPrecioLine === true,
    );
    let insertAt = -1;
    if (posIdx >= 0) {
      insertAt = posIdx + 1;
    } else if (hayFilasPrecio && !main.isGroupHeader) {
      // Sin unitario nuevo (solo descuentos %): IVA tras el P.UNIT (main).
      insertAt = 1;
    }

    if (insertAt > 0 && insertAt <= coreRows.length) {
      out.push(...coreRows.slice(0, insertAt));
      out.push(taxRow);
      out.push(...coreRows.slice(insertAt));
    } else {
      out.push(...coreRows);
      out.push(taxRow);
    }
    out.push(...tail);
  }

  return out;
}

const snapshotClampMoney = (value: number): number =>
  Math.max(0, Math.round(value));

export function toVentaDescuentosSnapshot(
  breakdown: DiscountBreakdown,
  cart?: CartItem[],
  globalDiscount?: LineDiscount | null,
) {
  if (breakdown.totalDescuentoItems <= 0 && breakdown.descuentoGlobal <= 0) {
    return undefined;
  }

  const lineDetails = breakdown.lines.map((line, index) => {
    const cartItem = cart?.[index];
    const qty = Math.max(1, line.cantidad ?? 1);
    const unitAfterItem =
      line.precioUnitarioFinal ??
      snapshotClampMoney(line.netoAntesGlobal / qty);
    return {
      productoId: cartItem?.id ?? line.lineId.split("#")[0] ?? line.lineId,
      variantKey: cartItem?.variantKey,
      modifierKey: cartItem?.modifierKey,
      nombre: line.nombre,
      bruto: line.bruto,
      netoFinal: line.netoItem,
      cantidad: qty,
      precioUnitarioFinal: unitAfterItem,
      precioUnitarioOriginal: line.precioUnitarioOriginal,
    };
  });

  const breakdownSnapshot: VentaDescuentosBreakdownSnapshot = {
    subtotalBruto: breakdown.subtotalBruto,
    totalDescuentoItems: breakdown.totalDescuentoItems,
    descuentoGlobal: breakdown.descuentoGlobal,
    globalDiscount: globalDiscount ?? null,
    lines: breakdown.lines.map((line) => ({
      lineId: line.lineId,
      nombre: line.nombre,
      bruto: line.bruto,
      netoAntesGlobal: line.netoAntesGlobal,
      descuentoItem: line.descuentoItem,
      netoItem: line.netoItem,
      descuentoGlobalAsignado: line.descuentoGlobalAsignado,
      descuentoTotalLinea: line.descuentoTotalLinea,
      precioUnitarioOriginal: line.precioUnitarioOriginal,
      precioUnitarioFinal: line.precioUnitarioFinal,
      cantidad: line.cantidad,
      ajusteAplicado: line.ajusteAplicado,
    })),
  };

  return {
    totalDescuentoItems: breakdown.totalDescuentoItems,
    descuentoGlobal: breakdown.descuentoGlobal,
    globalDiscountPct:
      globalDiscount?.type === "%" ? globalDiscount.value : undefined,
    lineAdjustments: breakdown.lines
      .filter((l) => l.descuentoTotalLinea > 0 || l.ajusteAplicado)
      .map((l) => ({
        nombre: l.nombre,
        descuentoTotalLinea: l.descuentoTotalLinea,
      })),
    lineDetails,
    breakdownSnapshot,
  };
}
